/**
 * WebSocket Message Classifier
 *
 * Extracted from useGlobalWebSocket.ts to enable unit testing.
 * Determines what events should be dispatched based on WebSocket message type.
 */

export interface WebSocketMessage {
  type?: string;
  event?: string;
  requestType?: string;
  message?: {
    chatId?: string;
    clientId?: string;
    body?: string;
    fromMe?: boolean;
  };
  data?: {
    id?: string;
    body?: string;
    lastMessage?: string;
    who?: "user" | "assistant";
    hasMedia?: boolean;
    timestamp?: string;
  };
  chatId?: string;
  clientId?: string;
  payload?: {
    fromMe?: boolean;
    clientID?: string;
    session?: string;
  };
}

export interface ClassificationResult {
  shouldDispatchSidebarUpdate: boolean;
  shouldDispatchMessageReceived: boolean;
  isSidebarUpdate: boolean;
}

/**
 * Classifies a WebSocket message to determine what events should be dispatched.
 *
 * Key rules:
 * 1. Sidebar updates (type="chat" with lastMessage but no body) update the sidebar
 * 2. Sidebar updates with who="assistant" ALSO dispatch websocket-message-received
 *    because that is how AI responses arrive in the conversation view
 * 3. Sidebar updates with who="user" do NOT dispatch websocket-message-received
 *    because user messages already arrive via optimistic updates + HTTP confirmation
 * 4. Direct message events always dispatch websocket-message-received
 */
export function classifyWebSocketMessage(
  data: WebSocketMessage,
): ClassificationResult {
  // Detect sidebar update: type="chat" with data but no body/hasMedia (sidebar metadata only)
  const isSidebarUpdate =
    data.type === "chat" &&
    !data.message &&
    !!data.data &&
    !data.data.body &&
    !data.data.hasMedia;

  // Sidebar updates should dispatch sidebar event
  const shouldDispatchSidebarUpdate = isSidebarUpdate;

  // Sidebar updates with who="assistant" also dispatch message-received
  // because AI responses arrive via this path
  const hasSidebarAssistantMessage =
    isSidebarUpdate &&
    !!data.data?.lastMessage &&
    data.data?.who === "assistant";

  // Determine if this is a direct message event
  const isMessageEvent =
    data.event === "message.received" ||
    data.type === "message.received" ||
    data.type === "message" ||
    data.requestType === "userMessage" ||
    (!!data.message && (!!data.message.chatId || !!data.message.clientId));

  // type="chat" that is NOT a sidebar update (has body or hasMedia) is a message
  const isChatWithMessageContent = data.type === "chat" && !isSidebarUpdate;
  // Dispatch message-received when:
  // 1. It's NOT a sidebar-only update, OR
  // 2. It's a sidebar update with an ASSISTANT message (AI responses)
  const shouldDispatchMessageReceived =
    (!isSidebarUpdate || hasSidebarAssistantMessage) &&
    (isMessageEvent || isChatWithMessageContent || hasSidebarAssistantMessage);

  return {
    shouldDispatchSidebarUpdate,
    shouldDispatchMessageReceived,
    isSidebarUpdate,
  };
}
